import React, { } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Calendar,
  Clock,
  Users,
  Video,
  Mail,
  MapPin,
  Building2,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface InterviewViewDialogProps {
  interview: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateStatus: (status: string) => void;
  onSendReminder: (interview: any) => void;
  onReschedule: (interview: any) => void;
  pendingStatus: string | null;
  isDirty: boolean;
  onSave: () => void;
  onEdit: (interview: any) => void;
  onDelete: (interview: any) => void;
  canDeleteInterviews?: boolean;
  canEditInterviews?: boolean;
}

const InterviewViewDialog: React.FC<InterviewViewDialogProps> = ({
  interview,
  open,
  onOpenChange,
  onUpdateStatus,
  onSendReminder,
  onReschedule,
  isDirty,
  onSave,
  onDelete,
  canDeleteInterviews = false,
  canEditInterviews = false
}) => {
  const interviewStatuses = [
    { id: 'scheduled', name: 'Scheduled', color: '#3B82F6' },
    { id: 'confirmed', name: 'Confirmed', color: '#8B5CF6' },
    { id: 'completed', name: 'Completed', color: '#10B981' },
    { id: 'cancelled', name: 'Cancelled', color: '#EF4444' },
    { id: 'rescheduled', name: 'Rescheduled', color: '#F59E0B' },
  ];

  // Count how many times the interview has been rescheduled
  const getRescheduleCount = (interview: any) => {
    if (!interview?.timeline) return 0;
    return interview.timeline.filter((event: any) =>
      event.type === 'status_change' &&
      event.metadata?.newStatus === 'rescheduled'
    ).length;
  };

  const rescheduleCount = getRescheduleCount(interview);
  const maxRescheduleReached = rescheduleCount >= 2;

  const getStatusColor = (status: any) => {
    const statusStr = getStatusString(status);
    // First try to find the status in the API statuses
    if (interviewStatuses && interviewStatuses.length > 0) {
      const statusObj = interviewStatuses.find((s: any) => s.name === statusStr || s.id === statusStr);
      if (statusObj && statusObj.color) {
        return statusObj.color;
      }
    }
    // Fallback to hardcoded colors
    switch (statusStr) {
      case 'scheduled':
        return '#3B82F6'; // blue
      case 'confirmed':
        return '#8B5CF6'; // purple
      case 'completed':
        return '#10B981'; // green
      case 'offered':
        return '#F59E0B'; // orange
      case 'hired':
        return '#059669'; // dark green
      case 'cancelled':
        return '#EF4444'; // red
      case 'rescheduled':
        return '#F59E0B'; // yellow
      default:
        return '#6B7280'; // gray
    }
  };

  const getStatusBadgeStyle = (status: any) => {
    const statusStr = getStatusString(status);
    let color = getStatusColor(statusStr);
    if (interviewStatuses && interviewStatuses.length > 0) {
      const statusObj = interviewStatuses.find((s: any) => s.id === statusStr || s.name === statusStr);
      if (statusObj && statusObj.color) {
        color = statusObj.color;
      }
    }
    return {
      backgroundColor: color + '10',
      color: color,
      borderColor: color + '40'
    };
  };

  const getCandidateName = (candidate: any) => {
    if (!candidate) return 'Candidate Deleted';
    if (typeof candidate === 'object') {
      const firstName = candidate.firstName || '';
      const lastName = candidate.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || 'Unknown Candidate';
    }
    return candidate;
  };

  const getCandidateEmail = (candidate: any) => {
    if (!candidate) return '';
    if (typeof candidate === 'object') {
      return candidate.email || '';
    }
    return '';
  };

  const getCandidateInitials = (candidate: any) => {
    if (!candidate) return 'CD'; // Candidate Deleted
    if (typeof candidate === 'object') {
      const first = candidate.firstName ? candidate.firstName[0] : '';
      const last = candidate.lastName ? candidate.lastName[0] : '';
      return (first + last).toUpperCase() || 'N/A';
    }
    // fallback for string
    const parts = String(candidate).split(' ');
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
  };

  const getDepartmentName = (department: any) => {
    if (!department) return 'Unknown Department';
    if (typeof department === 'string') return department;
    if (department && typeof department === 'object') return department.name || 'Unknown Department';
    return 'Unknown Department';
  };

  const getInterviewerNames = (interviewers: any[]) => {
    if (!Array.isArray(interviewers)) return 'No Interviewers';
    if (interviewers.length === 0) return 'No Interviewers';

    return interviewers.map((interviewer: any) => {
      if (!interviewer) return 'Unknown Interviewer';
      if (typeof interviewer === 'string') return interviewer;
      if (interviewer && typeof interviewer === 'object') {
        return interviewer.user ? `${interviewer.user.firstName} ${interviewer.user.lastName}` : interviewer.name || 'Unknown Interviewer';
      }
      return 'Unknown Interviewer';
    }).join(', ');
  };

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd-MM-yyyy');
    } catch {
      return dateStr;
    }
  };

  function getStatusString(status: any) {
    if (!status) return '';
    if (typeof status === 'string') return status;
    if (typeof status === 'object' && status !== null && 'name' in status) return status.name;
    return String(status);
  }

  function getStatusDisplayName(status: any) {
    const statusString = typeof status === 'string'
      ? status
      : (typeof status === 'object' && status !== null && 'name' in status)
        ? (status as any).name
        : String(status);

    const statusObj = interviewStatuses.find((s: any) => s.name === statusString || s.id === statusString);
    if (statusObj) {
      return statusObj.name || (statusString.charAt(0).toUpperCase() + statusString.slice(1));
    }
    return statusString.charAt(0).toUpperCase() + statusString.slice(1);
  }

  // Helper function to check if status is cancelled
  const isCancelledStatus = (status: any) => {
    const statusStr = getStatusString(status);
    return statusStr.toLowerCase().includes('cancel') ||
      statusStr.toLowerCase().includes('terminated') ||
      statusStr.toLowerCase().includes('cancelled');
  };

  // Helper function to check if interview is completed
  const isCompletedStatus = (status: any) => {
    const statusStr = getStatusString(status);
    return statusStr.toLowerCase() === 'completed' ||
      statusStr.toLowerCase() === 'complete' ||
      statusStr.toLowerCase() === 'hired';
  };

  // Helper function to validate status transition
  const validateStatusTransition = (currentStatus: any, newStatus: any) => {
    const currentStatusStr = getStatusString(currentStatus).toLowerCase();
    const newStatusStr = getStatusString(newStatus).toLowerCase();

    // Rule 1: Once "Scheduled", cannot go back to "Scheduled" - schedule is permanently disabled
    if (newStatusStr === 'scheduled' && currentStatusStr !== 'scheduled') {
      return 'Cannot schedule interview again. Once an interview is scheduled, it cannot be re-scheduled.';
    }

    // Rule 2: Once "Confirmed", cannot be confirmed again
    if (newStatusStr === 'confirmed' && currentStatusStr === 'confirmed') {
      return 'Interview is already confirmed. Cannot confirm the same interview twice.';
    }

    // Rule 3: Once "Confirmed", cannot go back to "Scheduled"
    if (newStatusStr === 'scheduled' && currentStatusStr === 'confirmed') {
      return 'Cannot return to "Scheduled" status once interview is confirmed.';
    }

    // Rule 4: Reschedule is only allowed before confirmation
    if (newStatusStr === 'rescheduled' && currentStatusStr === 'confirmed') {
      return 'Cannot reschedule interview after it has been confirmed.';
    }

    // Rule 5: Once "Completed", all status changes are disabled
    if (currentStatusStr === 'completed') {
      return 'Cannot change status for completed interviews. Once an interview is completed, no further status changes are allowed.';
    }

    // Rule 6: Cannot move to "Completed" from "Scheduled" (must be confirmed first)
    if (newStatusStr === 'completed' && currentStatusStr === 'scheduled') {
      return 'Interview must be confirmed before it can be marked as completed.';
    }

    // Rule 7: Cannot move to "Offered" or "Hired" from "Scheduled" (must be completed first)
    if ((newStatusStr === 'offered' || newStatusStr === 'hired') && currentStatusStr === 'scheduled') {
      return 'Interview must be completed before moving to offered or hired status.';
    }

    // Rule 8: Cannot move to "Hired" from "Confirmed" (must be completed first)
    if (newStatusStr === 'hired' && currentStatusStr === 'confirmed') {
      return 'Interview must be completed before moving to hired status.';
    }

    // Rule 9: Cannot move to "Offered" from "Confirmed" (must be completed first)
    if (newStatusStr === 'offered' && currentStatusStr === 'confirmed') {
      return 'Interview must be completed before moving to offered status.';
    }

    // Rule 10: Once "Offered" or "Hired", no further status changes allowed
    if (currentStatusStr === 'offered' || currentStatusStr === 'hired') {
      return 'Cannot change status for offered or hired interviews.';
    }

    return null; // No validation error
  };

  // Helper function to check if a status option should be disabled
  const isStatusOptionDisabled = (status: any) => {
    const currentStatus = getStatusString(interview.status);
    const newStatus = getStatusString(status);

    // Check if this is the current status
    if (currentStatus === newStatus || currentStatus === status.id || currentStatus === status.name) {
      return true;
    }

    // Check validation rules
    const validationError = validateStatusTransition(interview.status, status);
    if (validationError) {
      return true;
    }

    // Disable reschedule if limit reached
    if (status.name === 'rescheduled' && maxRescheduleReached) {
      return true;
    }

    return false;
  };


  // Helper function to get disabled reason for a status option
  const getStatusDisabledReason = (status: any) => {
    const currentStatus = getStatusString(interview.status);
    const newStatus = getStatusString(status);

    // Check if this is the current status
    if (currentStatus === newStatus || currentStatus === status.id || currentStatus === status.name) {
      return 'Current status';
    }

    // Check validation rules
    const validationError = validateStatusTransition(interview.status, status);
    if (validationError) {
      return validationError;
    }

    // Disable reschedule if limit reached
    if (status.name === 'rescheduled' && maxRescheduleReached) {
      return 'Maximum reschedules reached (2 times)';
    }

    return null;
  };


  if (!interview) return null;



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Interview Details</DialogTitle>
          <DialogDescription>Detailed view of the selected interview</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {interview && (
              <>
                {isCancelledStatus(interview.status) && (
                  <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <XCircle className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-red-800">Interview Cancelled</h4>
                        <p className="text-sm text-red-600 mt-1">
                          This interview has been cancelled and cannot be rescheduled or updated.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {!interview.candidate && (
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-orange-800">Candidate Deleted</h4>
                        <p className="text-sm text-orange-600 mt-1">
                          The candidate for this interview has been deleted. You can still view the interview details but cannot make changes.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {isCompletedStatus(interview.status) && (
                  <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-green-800">Interview Completed</h4>
                        <p className="text-sm text-green-600 mt-1">
                          This interview has been completed. No further status changes are allowed.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {getStatusString(interview.status).toLowerCase() === 'offered' && (
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-orange-800">Position Offered</h4>
                        <p className="text-sm text-orange-600 mt-1">
                          This candidate has been offered the position. The status will automatically transition to "Hired" shortly.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {getStatusString(interview.status).toLowerCase() === 'hired' && (
                  <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-green-800">Candidate Hired</h4>
                        <p className="text-sm text-green-600 mt-1">
                          This candidate has been successfully hired! No further status changes are allowed.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {maxRescheduleReached && !isCancelledStatus(interview.status) && (
                  <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-yellow-800">Maximum Reschedules Reached</h4>
                        <p className="text-sm text-yellow-600 mt-1">
                          This interview has already been rescheduled {rescheduleCount} times and cannot be rescheduled again.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className={`bg-gradient-to-r rounded-xl p-6 border shadow-sm ${isCancelledStatus(interview.status)
                  ? 'from-gray-50 to-gray-100 border-gray-200'
                  : 'from-blue-50 to-indigo-50 border-blue-100'
                  }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg ${isCancelledStatus(interview.status) ? 'bg-gray-500' : 'bg-blue-600'
                        }`}>
                        {getCandidateInitials(interview.candidate)}
                      </div>
                      <div className={isCancelledStatus(interview.status) ? 'opacity-75' : ''}>
                        <h1 className="text-3xl font-bold text-gray-900 mb-1">
                          {getCandidateName(interview.candidate)}
                        </h1>
                        <p className="text-gray-600 text-base font-medium">
                          {interview.jobPostingTitle
                            ? interview.jobPostingTitle
                            : (interview.position
                              ? (typeof interview.position === 'object'
                                ? interview.position.title
                                : interview.position)
                              : (interview.candidate && typeof interview.candidate === 'object' && interview.candidate.position
                                ? (typeof interview.candidate.position === 'object'
                                  ? interview.candidate.position.title
                                  : interview.candidate.position)
                                : 'Position not specified'))}
                        </p>
                        {getCandidateEmail(interview.candidate) && (
                          <p className="text-gray-500 text-sm flex items-center gap-2 mt-1">
                            <Mail className="h-4 w-4" />
                            {getCandidateEmail(interview.candidate)}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge
                            variant="outline"
                            className="px-3 py-1 text-sm font-medium"
                            style={getStatusBadgeStyle(interview.status)}
                          >
                            {getStatusDisplayName(interview.status)}
                          </Badge>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDateDisplay(interview.interviewDate)} at {interview.interviewTime}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canEditInterviews && !isCancelledStatus(interview.status) && !isCompletedStatus(interview.status) && interview.candidate && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <Clock className="h-4 w-4" />
                              Update Status
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {interviewStatuses.map((status: any) => (
                              <DropdownMenuItem
                                key={status.id}
                                onClick={() => {
                                  if (status.name === 'rescheduled') {
                                    if (maxRescheduleReached) {
                                      toast.error('Cannot reschedule more than 2 times');
                                      return;
                                    }
                                    onReschedule(interview);
                                  } else {
                                    onUpdateStatus(status.id);
                                  }
                                }}
                                disabled={isStatusOptionDisabled(status)}
                                className={isStatusOptionDisabled(status) ? 'opacity-50 cursor-not-allowed' : ''}
                                title={getStatusDisabledReason(status) || undefined}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="capitalize">
                                    Mark as {status.name.charAt(0).toUpperCase() + status.name.slice(1)}
                                  </span>
                                </div>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                      {/* Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="flex items-center gap-2">
                            <MoreHorizontal className="h-4 w-4" />
                            Actions
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!isCancelledStatus(interview.status) && interview.candidate && (
                            <DropdownMenuItem onClick={() => onSendReminder(interview)}>
                              <div className="flex items-center gap-2 text-blue-600">
                                <Mail className="h-4 w-4" />
                                Send Reminder
                              </div>
                            </DropdownMenuItem>
                          )}
                          {canDeleteInterviews && (
                            <DropdownMenuItem onClick={() => onDelete(interview)}>
                              <div className="flex items-center gap-2 text-red-600">
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </div>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {isDirty && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={onSave}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Save Changes
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-blue-50 rounded-xl p-6 shadow-sm border border-blue-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Interview Details</h2>
                    </div>
                    <div className="space-y-5">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <Building2 className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-1">Department</p>
                          <p className="font-semibold text-gray-900">{getDepartmentName(interview.department)}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <Video className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-1">Interview Type</p>
                          <p className="font-semibold text-gray-900">{interview.interviewType}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-1">Interviewers</p>
                          <p className="font-semibold text-gray-900">{getInterviewerNames(interview.interviewers)}</p>
                        </div>
                      </div>
                      {getCandidateEmail(interview.candidate) && (
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Mail className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">Candidate Email</p>
                            <p className="font-semibold text-gray-900">{getCandidateEmail(interview.candidate)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location Card */}
                  {(() => {
                    const location = interview.location || '';
                    // Regex to find a URL in the location string
                    const urlRegex = /(https?:\/\/[\w\-._~:/?#[\]@!$&'()*+,;=%]+)|(www\.[\w\-._~:/?#[\]@!$&'()*+,;=%]+)/gi;
                    const urls = location.match(urlRegex) || [];
                    // Remove URLs from the location string to get the physical location
                    const physicalLocation = location.replace(urlRegex, '').replace(/\s+\|\s+/, '').trim();
                    return (
                      <div className="bg-yellow-50 rounded-xl p-6 shadow-sm border border-yellow-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                            <MapPin className="h-5 w-5 text-yellow-600" />
                          </div>
                          <h2 className="text-xl font-bold text-gray-900">Location</h2>
                        </div>
                        {physicalLocation && (
                          <div className="mb-2">
                            <span className="font-semibold text-gray-900 text-base">{physicalLocation}</span>
                          </div>
                        )}
                        {urls.length > 0 && (
                          <div className="flex flex-col gap-2">
                            {(urls as string[]).map((url: string, idx: number) => (
                              <div key={idx} className="bg-white rounded-lg p-2 shadow-sm border border-blue-200 inline-block w-fit mb-2">
                                <a
                                  href={url.startsWith('http') ? url : `https://${url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-700 underline font-medium hover:text-blue-900 transition-colors"
                                >
                                  {url}
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                        {!physicalLocation && urls.length === 0 && (
                          <span className="font-semibold text-gray-900 text-base">Not specified</span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Timeline Card */}
                <div className="bg-green-50 rounded-xl p-6 shadow-sm border border-green-100 mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <Clock className="h-5 w-5 text-green-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Timeline</h2>
                  </div>
                  <div className="space-y-4">
                    {interview.timeline && interview.timeline.length > 0 ? (
                      interview.timeline.map((event: any, idx: number) => (
                        <div key={event._id || idx} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex items-start gap-3">
                          <div className="w-3 h-3 rounded-full bg-blue-500 mt-2" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-gray-900">
                                {event.status || event.title || 'Event'} - {getCandidateName(interview.candidate)}
                              </p>
                              <div className="flex gap-2">
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  {formatDateDisplay(event.date) || (event.createdAt ? formatDateDisplay(event.createdAt) : '')}
                                </Badge>
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                  {event.date ? new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                                    (event.createdAt ? new Date(event.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')}
                                </Badge>
                              </div>
                            </div>
                            {event.description && <p className="text-xs text-gray-600 mb-1">{event.description}</p>}
                            {event.createdBy && (
                              <p className="text-xs text-gray-500">
                                by {event.createdBy.firstName} {event.createdBy.lastName}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">No timeline events available.</p>
                    )}
                  </div>
                </div>

                {/* Notes Card */}
                <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100 mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                      <FileText className="h-5 w-5 text-purple-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Notes</h2>
                  </div>
                  <p className="font-semibold text-gray-900 text-base">{interview.notes || 'No notes available for this interview.'}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewViewDialog; 